'use client';

import { useState, useEffect } from 'react';
import { cn } from '@/core/utils/cn';

interface NettoBruttoToggleProps {
  className?: string;
  size?: 'sm' | 'md';
}

export function getPriceDisplayMode(defaultMode: 'netto' | 'brutto' = 'brutto'): 'netto' | 'brutto' {
  if (typeof window === 'undefined') return defaultMode;
  const saved = localStorage.getItem('stag-price-mode');
  if (saved === 'netto' || saved === 'brutto') return saved;
  return defaultMode;
}

export function setPriceDisplayMode(mode: 'netto' | 'brutto') {
  if (typeof window === 'undefined') return;
  localStorage.setItem('stag-price-mode', mode);
  window.dispatchEvent(new CustomEvent('stag-price-mode-change', { detail: mode }));
}

export default function NettoBruttoToggle({ className, size = 'md' }: NettoBruttoToggleProps) {
  const [mode, setMode] = useState<'netto' | 'brutto'>('brutto');

  useEffect(() => {
    setMode(getPriceDisplayMode('brutto'));

    const handleModeChange = (e: any) => {
      if (e.detail === 'netto' || e.detail === 'brutto') {
        setMode(e.detail);
      }
    };

    window.addEventListener('stag-price-mode-change', handleModeChange);
    return () => window.removeEventListener('stag-price-mode-change', handleModeChange);
  }, []);

  const toggle = (newMode: 'netto' | 'brutto') => {
    setMode(newMode);
    setPriceDisplayMode(newMode);
  };

  const isSm = size === 'sm';

  return (
    <div className={cn('inline-flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200 shadow-inner', className)}>
      <button
        type="button"
        onClick={() => toggle('brutto')}
        className={cn(
          'rounded-lg font-bold transition-all text-xs',
          isSm ? 'px-2.5 py-1' : 'px-3 py-1.5',
          mode === 'brutto'
            ? 'bg-white text-slate-900 shadow-sm border border-slate-200'
            : 'text-slate-500 hover:text-slate-900'
        )}
      >
        Brutto
      </button>
      <button
        type="button"
        onClick={() => toggle('netto')}
        className={cn(
          'rounded-lg font-bold transition-all text-xs',
          isSm ? 'px-2.5 py-1' : 'px-3 py-1.5',
          mode === 'netto'
            ? 'bg-blue-600 text-white shadow-sm'
            : 'text-slate-500 hover:text-slate-900'
        )}
      >
        Netto
      </button>
    </div>
  );
}
